'use client';

import SubNavTab from '@/components/subNavTab/SubNavTab';
import useUrlQuery from '@/hooks/useUrlQuery';
import URL_QUERY_KEYS from '@/constants/urlQueryKeys';
import PATHS from '@/constants/path';
import { useGetCategoryList } from '@/services/category/queries';
import { useEffect, useSyncExternalStore } from 'react';
import { useRouter, usePathname } from 'next/navigation';

const emptySubscribe = () => () => {};

export default function MenuCategoryTab() {
  const router = useRouter();
  const pathname = usePathname();
  const { getUrlQuery, replaceUrlQuery, replaceUrlQueries } = useUrlQuery();
  const menuIdQuery = getUrlQuery({ key: URL_QUERY_KEYS.CATEGORY_ID });
  // useSearchParams는 레이아웃 하위 클라이언트 컴포넌트의 SSR 시점에 비어있을 수 있어,
  // 활성 탭(activeId) 기준이 서버/클라이언트 첫 렌더에서 갈리면 hydration mismatch가 난다.
  // 마운트 이후에만 활성 탭을 계산해 첫 렌더(서버=클라)를 일치시킨다.
  const isMounted = useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );
  const activeMenuId = isMounted ? Number(menuIdQuery) : NaN;
  const isInfoPage = pathname.startsWith(PATHS.MENU.INFO);
  const isListPage = pathname.startsWith(PATHS.MENU.LIST);

  const { data: categoryListResponse } = useGetCategoryList();
  const categoryList = categoryListResponse?.data;

  useEffect(() => {
    if (!menuIdQuery && !isInfoPage && categoryList && categoryList.length > 0) {
      replaceUrlQuery({
        key: URL_QUERY_KEYS.CATEGORY_ID,
        value: String(categoryList[0].id),
      });
    }
  }, [menuIdQuery, replaceUrlQuery, isInfoPage, categoryList]);

  if (isInfoPage) return null;

  const handleClickTab = ({ id }: { id: string | number }) => {
    if (isListPage) {
      // shallow: router.replace로 두면 쿼리만 바뀌어도 RSC를 다시 받아오는데,
      // menu/list는 서버에서 카테고리·메뉴 API를 await한 뒤 응답한다. 활성 탭은 useSearchParams
      // 기준이라 그 왕복이 끝날 때까지 밑줄조차 움직이지 않아, 회선이 느린 앱(WebView)에서는
      // 탭이 눌리지 않는 것처럼 보인다. 메뉴 목록은 client에서 categoryId로 다시 조회하므로
      // 서버 재렌더 없이 history만 갱신해 즉시 반영한다.
      // 검색은 카테고리와 무관하게(통합) 조회하므로, 탭을 고르면 검색을 끝내고 그 카테고리를 보여준다.
      // 검색어를 남겨두면 탭이 눌려도 목록이 그대로라 눌리지 않은 것처럼 보인다.
      replaceUrlQueries({
        entries: {
          [URL_QUERY_KEYS.CATEGORY_ID]: String(id),
          [URL_QUERY_KEYS.MENU_KEYWORD]: null,
        },
        shallow: true,
      });
      // shallow는 네비게이션이 아니라 스크롤이 유지된다. 기존 동작(카테고리 전환 시 목록 최상단)
      // 을 지키기 위해 직접 올린다.
      window.scrollTo({ top: 0 });
      return;
    }
    router.push(`${PATHS.MENU.LIST}?${URL_QUERY_KEYS.CATEGORY_ID}=${id}`);
  };

  return (
    <SubNavTab
      tabList={categoryList?.map(({ id, name }) => ({ id, label: name }))}
      activeId={activeMenuId}
      onTabClick={handleClickTab}
    />
  );
}
